import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface UseGoalsEnabledResult {
  goalsEnabled: boolean;
  loading: boolean;
  setGoalsEnabled: (enabled: boolean) => Promise<void>;
}

export function useGoalsEnabled(): UseGoalsEnabledResult {
  const { user } = useAuth();
  const { toast } = useToast();
  const [goalsEnabled, setGoalsEnabledState] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPreference = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('goals_enabled')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        // Default to true if null
        setGoalsEnabledState(data?.goals_enabled ?? true);
      } catch (error) {
        console.error('Error loading goals preference:', error);
        setGoalsEnabledState(true);
      } finally {
        setLoading(false);
      }
    };

    loadPreference();
  }, [user?.id]);

  const setGoalsEnabled = async (enabled: boolean) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ goals_enabled: enabled })
        .eq('id', user.id);

      if (error) throw error;

      setGoalsEnabledState(enabled);
      
      toast({
        title: enabled ? 'Reading goals enabled' : 'Reading goals disabled',
        description: enabled 
          ? 'Your reading goals will now appear on your dashboard.'
          : 'Reading goals are now hidden from your dashboard.',
      });
    } catch (error) {
      console.error('Error updating goals preference:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not update your preference. Please try again.',
      });
    }
  };

  return {
    goalsEnabled,
    loading,
    setGoalsEnabled
  };
}
